import { useState, useRef, useEffect, useMemo } from 'react';
import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { SSRConfig, useTranslation } from 'next-i18next';
import upperFirst from 'lodash/upperFirst';
import get from 'lodash/get';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { AxiosError, AxiosResponseHeaders } from 'axios';
import { requestWithCache } from '../../../core/request';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import planConfigSlice from '../../../core/planConfig/slice';
import { isValidHotel, Hotel } from '../../../core/isValidHotel';
import ContentWrapper from '../../../components/common/ContentWrapper';
import HotelGeneralInfo from '../../../components/hotel/HotelGeneralInfo';
import bookingSlice from '../../../core/booking/slice';
import analytics from '../../../core/analytics/analytics';
import {
  Affilation,
  COMMON_DATE_FORMAT,
  Category,
  Currency,
  DEFAULT_CHECK_IN_DATE,
  Locale,
  PaymentMethod,
} from '../../../core/constants';
import { generateLinks } from '../../../core/helpers/link';
import getTargetCampaignAndCampaignsForTags from '../../../core/helpers/getTargetCampaignAndCampaignsForTags';
import { calculateDiscountedPrice } from '../../../core/planConfig/updateSelectedMealPlansWithDiscount';
import FacilitiesAndRoom, {
  getOnsenDescription,
} from '../../../components/hotel/FacilitiesAndRoom/FacilitiesAndRoom';
import MiniSite from '../../../components/hotel/MiniSite/MiniSite';
import HotelFromApi, { HOTEL_STATUS } from '../../../../types/HotelFromApi';
import type FooterType from '../../../../types/Footer';
import type ApplicationStatuses from '../../../../types/ApplicationStatuses';
import type NotificationAlertType from '../../../../types/NotificationAlert';
import type MiniSiteContents from '../../../../types/MiniSiteContents';
import translator from '../../../core/helpers/translator';
import useStartPrices from '../../../core/hotel/useStartPrices';
import FAQs from '../../../components/faq/FAQs';
import { filterFaqsByLocale } from '../../../core/helpers/faq';
import { useGetPartnershipSettings } from '../../../core/partnership/use-partnership-settings';
import Access from '../../../components/hotel/Access';
import PageLayout from '../../../components/common/PageLayout';
import Message from '../../../components/hotel/Message';
import CheckoutIn from '../../../components/hotel/CheckoutIn';
import Policy from '../../../components/hotel/Policy';
import NotificationAlert from '../../../components/home/NotificationAlert';
import Availability from '../../../components/hotel/Availability';
import { useSelector } from '../../../core/redux/store';
import getGuestCount from '../../../core/booking/getGuestCount';
import HotelTopBanner from '../../../components/hotel/HeroBanner/HotelTopBanner';
import HotelCount from '../../../../types/HotelCount';
import { getAllHotels } from '../../../core/hotel/getAllHotels';
import checkHotelById from '../../../core/hotel/checkHotelById';
import Chatbot from '../../../components/hotel/Chatbot/Chatbot';
import { ChatWidgetSetting, TabistCareApiResponse } from '../../../../types/ChatWidgetSetting';

const GalleryModal = dynamic(
  () => import('../../../components/hotel/GalleryModalPanel/GalleryModal')
);
const RoomPhoto = dynamic(() => import('../../../components/hotel/RoomPhoto'));
const StickyBar = dynamic(() => import('../../../components/hotel/HotelGeneralInfo/StickyBar'));
const TLLMaintenanceStatus = dynamic(
  () => import('../../../components/booking/common/TLLMaintenanceStatus')
);
const MiniSiteRoomAvailabilities = dynamic(
  () => import('../../../components/hotel/MiniSite/MiniSiteRoomAvailabilities')
);

type Props = {
  hotel: Hotel | undefined;
  footer: FooterType | undefined;
  notificationAlert: NotificationAlertType | undefined;
  locale: string | undefined;
  miniSiteContents: MiniSiteContents | undefined;
  preview: boolean | null;
  tllMaintenanceStatus?: ApplicationStatuses[0];
  hotelCount: HotelCount | number | undefined;
  chatWidgetSetting: ChatWidgetSetting | null;
} & Metadata &
  SSRConfig;

export const getHotelPhotoCount = (hotel: Hotel | undefined) => {
  const categories = hotel?.photos?.categories;
  const categorizedPhotos = hotel?.photos?.categorizedPhotos;

  if (!categories || !categorizedPhotos) {
    return 0;
  }

  const reducer = (acc: number, currentCategory: string): number => {
    const photos = categorizedPhotos[currentCategory];

    if (photos && photos.length > 0) {
      return acc + photos.length;
    }

    return acc;
  };

  return categories.reduce(reducer, 0);
};

const HotelDetails: NextPage<Props> = ({
  hotel,
  notificationAlert,
  footer,
  locale,
  miniSiteContents,
  chatWidgetSetting,
  preview,
  tllMaintenanceStatus,
  hotelCount,
}) => {
  const dispatch = useAppDispatch();
  const { checkInDateISOString } = useSelector((state) => state.booking);
  const accessDivRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollToAccess = () => {
    accessDivRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const hasSomePolicy =
    !!hotel?.allowPayAtHotel ||
    !!hotel?.allowPrepay ||
    (typeof hotel?.payment === 'string' ? !!hotel?.payment : (hotel?.payment ?? []).length > 0) ||
    !!hotel?.precautions ||
    !!hotel?.noShowPolicy;
  const photoCount = getHotelPhotoCount(hotel);
  const { t } = useTranslation('hotel');
  const { query, replace, isReady } = useRouter();
  const affiliateId = query.affiliate_id as string;

  const { data: partnershipSettings, isInitialLoading: isPartnershipSettingsLoading } =
    useGetPartnershipSettings();

  const tabistPaymentTypes = useMemo(() => {
    if (process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER !== 'true')
      return hotel?.tabistPaymentTypes;

    if (!affiliateId) return hotel?.tabistPaymentTypes;
    if (isPartnershipSettingsLoading) return [];
    if (!partnershipSettings?.paymentTypes) return [];
    if (partnershipSettings?.paymentTypes?.length === 0) return hotel?.tabistPaymentTypes;

    return partnershipSettings?.paymentTypes?.map((type) => type.paymentType as PaymentMethod);
  }, [affiliateId, hotel?.tabistPaymentTypes, isPartnershipSettingsLoading, partnershipSettings]);

  const checkInDate = moment(checkInDateISOString).format(COMMON_DATE_FORMAT);
  const { startPricesList, isLoading: isStartPricesLoading } = useStartPrices({
    propertyIds: hotel?.id,
    date: checkInDate || DEFAULT_CHECK_IN_DATE,
    locale,
  });

  const startPrices = Array.isArray(startPricesList) ? startPricesList[0] : undefined;

  const isBookingEngineEnabled = !!hotel?.tllHotelCode;
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const { targetCampaign, campaignsForTags } = getTargetCampaignAndCampaignsForTags(
    hotel?.webCampaigns,
    isLoggedIn
  );
  const discountedAmount =
    hotel?.cheapestPrice && targetCampaign
      ? calculateDiscountedPrice(Number(hotel.cheapestPrice), targetCampaign)
      : 0;

  // NOTE: Reset Redux state from possible previous booking
  useEffect(() => {
    dispatch(planConfigSlice.actions.reset());
    dispatch(bookingSlice.actions.reset());
  }, [dispatch]);

  useEffect(() => {
    if (hotel) {
      analytics.google.ecommerce.track.item.detail(
        [
          {
            affiliation: Affilation.WEB,
            item_id: hotel?.id as string,
            item_name: hotel?.name as string,
            currency: Currency.JPY,
            discount: Number(hotel?.cheapestPrice) - discountedAmount,
            price: Number(hotel?.cheapestPrice),
            item_category: hotel?.tagRyokan ? Category.RYOKAN : Category.HOTEL,
          },
        ],
        discountedAmount
      );
    }
  }, [discountedAmount, hotel]);

  useEffect(() => {
    if (hotel && isReady) {
      const guestCount = getGuestCount(query?.guestPerRoom as string);
      analytics.facebook.track.view({
        content_type: 'hotel',
        checkin_date: query.checkInDate as string,
        checkout_date: query.checkOutDate as string,
        content_ids: hotel?.id || '',
        city: hotel?.address?.prefecture || undefined,
        region: hotel?.address?.region || undefined,
        country: locale === Locale.JAPANESE ? '日本' : 'Japan',
        num_rooms: query?.roomCount as unknown as number,
        num_adults: guestCount?.adultCount,
        num_children: Object.entries(guestCount).reduce((accumulator, [key, value]) => {
          const result = accumulator + key !== 'adultCount' ? value : 0;
          return result;
        }, 0),
      });
    }
  }, [
    hotel,
    isReady,
    locale,
    query.checkInDate,
    query.checkOutDate,
    query.guestPerRoom,
    query?.roomCount,
  ]);

  const checkInOut = hotel?.checkinCheckout?.split('\n');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    amenityFeature: [
      hotel?.internet && {
        '@type': 'LocationFeatureSpecification',
        value: upperFirst(hotel?.internet),
        name: t('hotel:facilities_and_room.internet'),
      },
      (hotel?.facilities?.publicBath?.value || hotel?.tagRyokan) && {
        '@type': 'LocationFeatureSpecification',
        value: upperFirst(getOnsenDescription(hotel).join('/')),
        name: t('hotel:facilities_and_room.onsen'),
      },
      hotel?.parking && {
        '@type': 'LocationFeatureSpecification',
        value: upperFirst(hotel?.parking),
        name: t('hotel:facilities_and_room.parking'),
      },
      hotel?.smokingDetails && {
        '@type': 'LocationFeatureSpecification',
        value: upperFirst(hotel?.smokingDetails),
        name: t('hotel:facilities_and_room.smoking'),
      },
      hotel?.meal && {
        '@type': 'LocationFeatureSpecification',
        value: upperFirst(hotel?.meal),
        name: t('hotel:facilities_and_room.meal'),
      },
      (hotel?.facilities?.others ?? []).length > 0 && {
        '@type': 'LocationFeatureSpecification',
        value: (hotel?.facilities?.others ?? [])
          .map((facility) => upperFirst(facility?.description))
          .join(' / '),
        name: t('hotel:facilities_and_room.others'),
      },
    ].filter((feature) => feature),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: hotel?.address?.latitude,
      longitude: hotel?.address?.longitude,
    },
    paymentAccepted:
      typeof hotel?.payment === 'string'
        ? hotel?.payment
            ?.split('\n')
            .map((text) => upperFirst(text))
            .join(locale === Locale.JAPANESE ? '、 ' : ', ')
        : hotel?.payment
            ?.map((key) => t(`enum:PaymentMethod.${key}`))
            .join(locale === Locale.JAPANESE ? '、 ' : ', '),
    checkinTime: checkInOut ? checkInOut[0]?.substring(0, 5) : undefined,
    checkoutTime: (checkInOut ?? []).length > 1 ? (checkInOut ?? [])[1] : undefined,
    image: hotel?.photos?.selectedPhotos.find((photo) => photo.selection === 'MAIN')?.url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: hotel?.address?.postalAddress,
      addressRegion: hotel?.address?.region,
      addressLocality: hotel?.address?.prefecture,
    },
    url: `${process.env.NEXT_PUBLIC_SITE_URL}/h/${hotel?.id}`,
    description: hotel?.accessSimple,
    name: hotel?.name,
  };

  // redirect to 404 if hotel is not whitelisted or blacklisted
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER !== 'true') return;
    if (!hotel) return;
    if (!partnershipSettings) return;

    const whitelistProperties = partnershipSettings?.whitelistPropertyIds?.map(
      (whitelist) => whitelist.propertyId
    );
    const blacklistProperties = partnershipSettings?.blacklistPropertyIds?.map(
      (blacklist) => blacklist.propertyId
    );

    if (whitelistProperties?.length) {
      // If whitelistProperties is set, only show the properties in the list
      const isWhitelisted = whitelistProperties.includes(hotel.id);

      if (!isWhitelisted) replace('/404');
    }

    if (blacklistProperties?.length) {
      // If blacklistProperties is set, do not show the properties in the list
      const isBlacklisted = blacklistProperties.includes(hotel.id);

      if (isBlacklisted) replace('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel, partnershipSettings]);

  const faqs = filterFaqsByLocale(hotel?.faqs, locale);

  const renderContents = () => {
    if (hotel && miniSiteContents)
      return (
        <MiniSite
          hotel={hotel}
          miniSiteContents={miniSiteContents}
          preview={preview}
          setIsModalOpen={setIsModalOpen}
          roomsTabContent={
            <MiniSiteRoomAvailabilities
              hotel={hotel}
              isTLLMaintenanceStatus={tllMaintenanceStatus}
              tabistPaymentTypes={tabistPaymentTypes}
              photoCount={photoCount}
              setIsModalOpen={setIsModalOpen}
            />
          }
          startPrices={startPricesList?.[0]}
          tabistPaymentTypes={tabistPaymentTypes}
          isStartPricesLoading={isStartPricesLoading}
        />
      );

    return (
      <main>
        <ContentWrapper className="sm:my-6 sm-max:px-0">
          <HotelTopBanner hotel={hotel} setIsModalOpen={setIsModalOpen} />
        </ContentWrapper>
        <ContentWrapper className="py-6 sm:pb-6 sm:pt-0">
          <HotelGeneralInfo
            scrollToAccess={scrollToAccess}
            hotel={hotel}
            campaignsForTags={campaignsForTags}
            isBookingEngineEnabled={isBookingEngineEnabled}
            startPrices={startPrices}
            isStartPricesLoading={isStartPricesLoading}
          />
        </ContentWrapper>
        {hotel?.message && (
          <ContentWrapper className="pb-6 text-gray-600">
            <Message
              message={hotel.message}
              hotelName={t('message.hotel_name', { name: hotel.name })}
            />
          </ContentWrapper>
        )}
        {isBookingEngineEnabled && hotel?.name ? (
          <div className="w-full bg-gray-50">
            <ContentWrapper className="py-10">
              {tllMaintenanceStatus ? (
                <TLLMaintenanceStatus
                  start_date={tllMaintenanceStatus.start_date}
                  end_date={tllMaintenanceStatus.end_date}
                />
              ) : (
                <Availability
                  hotelId={hotel.id}
                  isTLLMaintenanceStatus={tllMaintenanceStatus}
                  locale={locale}
                />
              )}
            </ContentWrapper>
          </div>
        ) : (
          <ContentWrapper>
            <RoomPhoto
              hotel={hotel}
              galleryEnabled={photoCount > 0}
              onGalleryClick={() => {
                analytics.google.ecommerce.track.viewGallery(hotel?.id);
                return setIsModalOpen(true);
              }}
            />
          </ContentWrapper>
        )}
        <ContentWrapper className="bg-gray-50 py-10 sm:bg-white">
          <FacilitiesAndRoom hotel={hotel} />
          {!!hotel?.checkinCheckout && <CheckoutIn title={hotel.checkinCheckout} />}
          {hasSomePolicy && (
            <div className="mt-10 sm:mt-20">
              <Policy
                cancellationPolicy={t('hotel:policy.cancel_policy_description')}
                tabistPaymentTypes={tabistPaymentTypes}
                propertyPaymentTypes={hotel?.propertyPaymentTypes}
                allowPayAtHotelForForeignGuests={hotel?.allowPayAtHotelForForeignGuests}
                creditcard={hotel?.creditcard}
                precautions={hotel?.precautions}
                noShowPolicy={hotel?.noShowPolicy}
              />
            </div>
          )}
        </ContentWrapper>
        <ContentWrapper className="pb-12 pt-10 sm:pb-28">
          <Access ref={accessDivRef} hotel={hotel} />
        </ContentWrapper>

        {faqs.length > 0 && (
          <ContentWrapper className="pb-12 sm:pb-28">
            <FAQs hotelId={hotel?.id} faqs={faqs} />
          </ContentWrapper>
        )}

        {!isBookingEngineEnabled && (
          <StickyBar
            hotelId={hotel?.id}
            hotelName={hotel?.name}
            bookingEngineExternalLink={hotel?.bookingEngineExternalLink}
            startPrices={startPrices}
            isStartPricesLoading={isStartPricesLoading}
          />
        )}
      </main>
    );
  };

  return (
    <PageLayout
      header={{
        showSearchIcon: true,
        shadow: true,
      }}
      footer={{
        content: footer,
      }}
      hotelCount={hotelCount}
    >
      <Head>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>
      {chatWidgetSetting?.is_enabled ? (
        <Chatbot
          propertyId={hotel?.id}
          propertyName={hotel?.name}
          locale={locale}
          chatbotKey={hotel?.chatbot?.key}
        />
      ) : (
        <Chatbot chatbotKey={hotel?.chatbot?.key} />
      )}

      {isModalOpen && hotel && <GalleryModal hotel={hotel} setIsModalOpen={setIsModalOpen} />}
      <div className="relative">
        {!!notificationAlert && (
          <NotificationAlert message={notificationAlert.message} url={notificationAlert.url} />
        )}
      </div>

      {renderContents()}
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' ||
    (process.env.NEXT_PUBLIC_STG_SSG_ON && process.env.VERCEL_GIT_COMMIT_REF === 'stg')
  ) {
    const fields = ['property_id', 'hotel_name'];
    const { hotels } = await getAllHotels(fields, Locale.JAPANESE);

    const paths = hotels.reduce(
      (accumulator, current) => {
        return [
          ...accumulator,
          ...Object.values(Locale).map((locale) => ({
            params: { hotel: current.id },
            locale,
          })),
        ];
      },
      [] as GetStaticPathsResult['paths']
    );

    return {
      paths,
      fallback: true,
    };
  }

  return {
    paths: [],
    fallback: true,
  };
};

const revalidate = process.env.REVALIDATE_HOTEL_DETAIL_PAGE
  ? Number(process.env.REVALIDATE_HOTEL_DETAIL_PAGE)
  : false;

export const getStaticProps: GetStaticProps<
  Props,
  {
    hotel: string;
  },
  { hotelId: string }
> = async ({ locale, params, preview = null, previewData }) => {
  const hotelId = params?.hotel;
  const isPreview = previewData?.hotelId === hotelId && preview;
  if (!hotelId) {
    throw new Error('No param "hotel"');
  }

  if (!(await checkHotelById(encodeURI(hotelId)))) {
    return { notFound: true, revalidate };
  }

  const hotelPromise = requestWithCache<HotelFromApi>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels/${encodeURI(
      hotelId
    )}?locale=${locale}&tabist=ok&include_faqs=true`,
    method: 'GET',
  });

  const notificationAlertPromise = requestWithCache<NotificationAlertType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/notification-alert?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const footerPromise = requestWithCache<FooterType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/footer?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const hotelCountPromise = requestWithCache<AxiosResponseHeaders & { 'x-total-count': number }>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels?locale=${Locale.JAPANESE}&tabist=ok`,
    method: 'HEAD',
  });

  const miniSiteContentsPromise = requestWithCache<MiniSiteContents>({
    url: `${
      process.env.NEXT_PUBLIC_BFF_URL
    }/mini-site/contents/${hotelId}?locale=${translator.locale.get(locale)}&status=${
      isPreview ? 'draft' : 'published'
    }`,
    method: 'GET',
  });

  const chatWidgetSettingPromise = process.env.NEXT_PUBLIC_CHATBOT_API_URL
    ? requestWithCache<TabistCareApiResponse<ChatWidgetSetting>>({
        url: `${process.env.NEXT_PUBLIC_CHATBOT_API_URL}/api/properties/${hotelId}`,
        headers: {
          'x-app-client-key': process.env.NEXT_PUBLIC_CHATBOT_APP_CLIENT_KEY as string,
          ...(process.env.NEXT_PUBLIC_CHATBOT_VERCEL_PROTECTION_BYPASS
            ? {
                'x-vercel-protection-bypass':
                  process.env.NEXT_PUBLIC_CHATBOT_VERCEL_PROTECTION_BYPASS,
              }
            : {}),
        },
        method: 'GET',
      }).catch((error) => {
        // API returns 404 if the chat widget is not configured for this property; treat that
        // as "disabled". Any other failure (timeout, 5xx, auth) is transient, so rethrow it
        // instead of silently persisting a disabled state for this statically generated page.
        if ((error as AxiosError)?.response?.status === 404) {
          return undefined;
        }
        throw error;
      })
    : Promise.resolve(undefined);

  const [hotel, notificationAlert, footer, hotelCount, miniSiteContents, chatWidgetSetting] =
    await Promise.all([
      hotelPromise,
      notificationAlertPromise,
      footerPromise,
      hotelCountPromise,
      miniSiteContentsPromise,
      chatWidgetSettingPromise,
    ]);

  if (
    !hotel ||
    !isValidHotel(hotel) ||
    hotel.status?.toLowerCase() === HOTEL_STATUS.PAUSED ||
    hotel.status?.toLowerCase() === HOTEL_STATUS.CHURNED // generate page if property status is Live or Draft
  ) {
    return { notFound: true, revalidate };
  }

  const ssrConfig = await serverSideTranslations(translator.locale.get(locale), [
    'common',
    'component',
    'enum',
    'hotel',
    'error',
  ]);

  const namespaces =
    // eslint-disable-next-line no-underscore-dangle
    locale && ssrConfig._nextI18Next.initialI18nStore[translator.locale.get(locale)];
  const url = new URL(`${process.env.NEXT_PUBLIC_SITE_URL}/h/${hotelId}`);

  const translationTargets = {
    hotelName: hotel?.name || '',
    siteName: get(namespaces.common, 'meta.site_name', ''),
    description: hotel?.accessSimple || '',
    'og:description': hotel?.accessSimple || '',
  };

  const translatedMetadata = await translator.batch.translate(translationTargets, locale);

  const metadata = {
    title: `${translatedMetadata?.hotelName || ''}${translatedMetadata?.hotelName && ' | '}${translatedMetadata?.siteName}`,
    'og:title': translatedMetadata?.hotelName || '',
    description: translatedMetadata.description || '',
    'og:description': translatedMetadata['og:description'] || '',
    'og:image': hotel?.photos?.selectedPhotos[0]?.url || '',
    'og:site_name': translatedMetadata?.siteName || '',
    'og:locale': locale,
    'og:url': `${url.origin}${locale !== Locale.JAPANESE ? `/${locale}` : ''}${url.pathname}`,
  };

  const contents = hotel?.photos?.selectedPhotos[0]?.url
    ? [
        {
          href: hotel?.photos?.selectedPhotos[0]?.url || '',
          as: 'image',
        },
      ]
    : [];

  return {
    props: {
      ...ssrConfig,
      hotel,
      notificationAlert,
      footer,
      locale,
      hotelCount: hotelCount['x-total-count'] && Number(hotelCount['x-total-count']),
      miniSiteContents,
      chatWidgetSetting:
        chatWidgetSetting?.success && chatWidgetSetting?.data ? chatWidgetSetting?.data : null,
      preview: isPreview,
      metadata: namespaces && locale && metadata,
      links: generateLinks(url, contents),
    },
    revalidate,
  };
};

export default HotelDetails;
